import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Check, Trash2, Wifi, WifiOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { notificationsAPI } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import { showGeneralNotification, showBookingNotification, getNotificationPermission } from '../services/pushNotifications';
import { toast } from 'sonner';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const dropdownRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const seenNotificationIds = useRef(new Set());
  const isInitialFetch = useRef(true);

  // Get appropriate notification function based on type
  const showBrowserNotification = useCallback((notif) => {
    if (getNotificationPermission() !== 'granted') return;
    
    const bookingTypes = ['new_booking', 'booking_confirmed', 'booking_cancelled', 'booking_completed'];
    
    if (bookingTypes.includes(notif.notification_type)) {
      showBookingNotification(
        notif.title,
        notif.message,
        () => { window.focus(); setIsOpen(true); }
      );
    } else {
      showGeneralNotification(
        notif.title || 'NC Help Hub',
        notif.message || 'You have a new notification',
        () => { window.focus(); setIsOpen(true); }
      );
    }
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback((isPriority = false) => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;

      const audioCtx = new AudioContext();
      
      if (isPriority) {
        // Priority: Triple bell chime for rides/urgent
        const playChime = (freq, delay) => {
          const osc = audioCtx.createOscillator();
          const gain = audioCtx.createGain();
          osc.connect(gain);
          gain.connect(audioCtx.destination);
          osc.frequency.value = freq;
          osc.type = 'sine';
          gain.gain.setValueAtTime(0.5, audioCtx.currentTime + delay);
          gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + delay + 0.3);
          osc.start(audioCtx.currentTime + delay);
          osc.stop(audioCtx.currentTime + delay + 0.3);
        };
        // Bell pattern: ding-ding-DING
        playChime(880, 0);
        playChime(880, 0.2);
        playChime(1100, 0.4);
      } else {
        // Regular: Pleasant two-tone chime
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.frequency.setValueAtTime(523.25, audioCtx.currentTime); // C5
        osc.frequency.setValueAtTime(659.25, audioCtx.currentTime + 0.1); // E5
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.4, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
        osc.start(audioCtx.currentTime);
        osc.stop(audioCtx.currentTime + 0.3);
      }
    } catch (e) {
      console.log('Sound notification failed:', e);
    }
  }, []);

  // Handle incoming WebSocket notification
  const handleWsNotification = useCallback((notification) => {
    if (!seenNotificationIds.current.has(notification.id)) {
      seenNotificationIds.current.add(notification.id);
      
      // Add to notifications list
      setNotifications(prev => [notification, ...prev].slice(0, 20));
      setUnreadCount(prev => prev + 1);
      
      // Play sound - priority for rides and new bookings
      const isPriority = notification.notification_type === 'new_booking' || 
                         notification.notification_type === 'ride_request' ||
                         notification.type === 'ride';
      playNotificationSound(isPriority);
      
      // Show browser notification
      showBrowserNotification(notification);
      
      // Show in-app toast
      toast(notification.title, {
        description: notification.message,
        duration: 5000,
      });
    }
  }, [showBrowserNotification, playNotificationSound]);

  // Initialize WebSocket connection with retry for mobile auth timing
  const connectWebSocket = useCallback(async (retryCount = 0) => {
    const maxRetries = 3;
    const retryDelay = 1000; // 1 second between retries
    
    try {
      // Get WebSocket token from the API (since httpOnly cookies can't be read by JS)
      const response = await fetch(`${window.location.origin}/api/auth/ws-token`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        // On mobile, auth cookie may not be set yet - retry after delay
        if (retryCount < maxRetries) {
          console.log(`WebSocket auth failed, retrying in ${retryDelay}ms (attempt ${retryCount + 1}/${maxRetries})`);
          setTimeout(() => connectWebSocket(retryCount + 1), retryDelay);
          return;
        }
        console.log('Failed to get WebSocket token after retries - user may not be logged in');
        return;
      }
      
      const data = await response.json();
      const token = data.ws_token;
      
      if (!token) {
        console.log('No WebSocket token received');
        return;
      }

      // Close existing connection if any
      if (wsRef.current) {
        wsRef.current.close();
      }

      // Determine WebSocket URL
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${window.location.host}/api/ws/notifications?token=${token}`;
      
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('Notification WebSocket connected');
        setWsConnected(true);
        
        // Start heartbeat
        heartbeatIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000); // Ping every 30 seconds
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'connected':
              // Initial connection - set unread count from server
              setUnreadCount(data.unread_count || 0);
              break;
              
            case 'notification':
              // New notification received
              handleWsNotification(data.data);
              break;
              
            case 'notification_read':
              // Single notification marked as read
              setNotifications(prev => 
                prev.map(n => n.id === data.notification_id ? { ...n, is_read: true } : n)
              );
              setUnreadCount(prev => Math.max(0, prev - 1));
              break;
              
            case 'all_read':
              // All notifications marked as read
              setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
              setUnreadCount(0);
              break;
              
            case 'pong':
              // Heartbeat response - connection is alive
              break;
              
            default:
              console.log('Unknown WS message type:', data.type);
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };
      
      wsRef.current.onclose = (event) => {
        console.log('Notification WebSocket closed:', event.code, event.reason);
        setWsConnected(false);
        
        // Clear heartbeat
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
        }
        
        // Reconnect after 5 seconds (unless intentionally closed)
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Attempting WebSocket reconnection...');
            connectWebSocket();
          }, 5000);
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
      
    } catch (e) {
      console.error('Failed to create WebSocket:', e);
      setWsConnected(false);
    }
  }, [handleWsNotification]);

  // Fetch notifications via HTTP (fallback/initial load)
  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        notificationsAPI.getAll(false, 20),
        notificationsAPI.getUnreadCount()
      ]);
      const newNotifications = notifRes.data || [];
      const newUnreadCount = countRes.data?.unread_count || 0;
      
      // Mark all fetched notifications as seen
      newNotifications.forEach(n => {
        seenNotificationIds.current.add(n.id);
      });
      
      isInitialFetch.current = false;
      setNotifications(newNotifications);
      setUnreadCount(newUnreadCount);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  // Initialize on mount - run only once
  useEffect(() => {
    // Fetch initial notifications
    fetchNotifications();
    
    // Connect WebSocket with slight delay for mobile auth timing
    // This gives time for the auth cookie to be fully set after login
    const wsConnectTimeout = setTimeout(() => {
      connectWebSocket();
    }, 500); // 500ms delay helps with mobile auth timing
    
    // Fallback polling every 30s if WebSocket isn't connected
    // Check wsRef.current?.readyState instead of wsConnected state to avoid dependency issues
    const pollInterval = setInterval(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        fetchNotifications();
      }
    }, 30000);
    
    return () => {
      clearTimeout(wsConnectTimeout);
      clearInterval(pollInterval);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
    };
  }, []); // Empty deps - run once on mount only

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAsRead = async (notificationId) => {
    try {
      // Try WebSocket first
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'mark_read',
          notification_id: notificationId
        }));
      } else {
        // Fallback to HTTP
        await notificationsAPI.markAsRead(notificationId);
        setNotifications(prev => 
          prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      // Try WebSocket first
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'mark_all_read' }));
      } else {
        // Fallback to HTTP
        await notificationsAPI.markAllAsRead();
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await notificationsAPI.delete(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      new_booking: '📅',
      booking_confirmed: '✅',
      booking_cancelled: '❌',
      booking_completed: '🎉',
      job_available: '📋',
      job_claimed: '🙋',
      job_assigned: '✨',
      worker_clocked_in: '⏰',
      worker_clocked_out: '🏠',
      new_message: '💬',
      payment_received: '💰',
      new_application: '📝',
      application_approved: '🎊',
      first_time_discount: '🎁',
      general: '🔔'
    };
    return icons[type] || '🔔';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-[var(--background-subtle)] transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-6 h-6 text-[var(--text-secondary)]" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        {/* WebSocket status indicator */}
        <span className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500' : 'bg-gray-400'}`} 
              title={wsConnected ? 'Real-time connected' : 'Polling mode'} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-[var(--background-paper)] rounded-xl shadow-xl border border-[var(--border-primary)] z-[9999] overflow-hidden"
            style={{ maxHeight: 'calc(100vh - 150px)' }}
          >
            <div className="p-4 border-b flex items-center justify-between bg-[var(--background-subtle)]">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-[var(--text-primary)]">Notifications</h3>
                {wsConnected ? (
                  <Wifi className="w-3 h-3 text-green-500" title="Real-time connected" />
                ) : (
                  <WifiOff className="w-3 h-3 text-gray-400" title="Polling mode" />
                )}
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-[#D4AF37] hover:text-blue-800 font-medium"
                >
                  Mark all as read
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-[var(--text-muted)]">
                  <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No notifications yet</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 border-b hover:bg-[var(--background-subtle)] transition-colors ${
                      !notification.is_read ? 'bg-[#D4AF37]/10' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-xl">{getNotificationIcon(notification.notification_type)}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium text-sm ${!notification.is_read ? 'text-[var(--text-primary)]' : 'text-[var(--text-primary)]'}`}>
                          {notification.title}
                        </p>
                        <p className="text-sm text-[var(--text-secondary)] mt-1">{notification.message}</p>
                        <p className="text-xs text-gray-400 mt-2">
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        {!notification.is_read && (
                          <button
                            onClick={() => handleMarkAsRead(notification.id)}
                            className="p-1 hover:bg-[var(--background-subtle)] rounded"
                            title="Mark as read"
                          >
                            <Check className="w-4 h-4 text-[var(--success)]" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(notification.id)}
                          className="p-1 hover:bg-[var(--background-subtle)] rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-[var(--error)]" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {notifications.length > 0 && (
              <div className="p-3 border-t bg-[var(--background-subtle)] text-center">
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                >
                  Close
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
